import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { SEOHead, BreadcrumbSchema } from '../components/SEO';
import { Calendar, User, Eye, ArrowLeft, Tag, Share2, Facebook, Twitter } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/blog/${slug}`);
        setPost(response.data);
        
        // Fetch related posts
        const allPosts = await axios.get(`${API_URL}/api/blog`);
        const related = allPosts.data
          .filter(p => p.slug !== slug && p.category === response.data.category)
          .slice(0, 2);
        setRelatedPosts(related);
      } catch (error) {
        console.error('Failed to fetch blog post:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-pulse text-emerald-600">Loading article...</div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex flex-col items-center justify-center h-[60vh]">
          <h1 className="font-serif text-2xl text-foreground mb-4">Article Not Found</h1>
          <Link to="/blog" className="text-emerald-600 hover:text-emerald-700">
            ← Back to Blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const breadcrumbs = [
    { name: 'Home', url: 'https://thehotelemerald.in/' },
    { name: 'Blog', url: 'https://thehotelemerald.in/blog' },
    { name: post.title, url: `https://thehotelemerald.in/blog/${slug}` }
  ];

  const shareUrl = `https://thehotelemerald.in/blog/${slug}`;

  return (
    <div className="min-h-screen bg-background" data-testid="blog-post-page">
      <SEOHead
        title={`${post.title} | The Hotel Emerald Blog`}
        description={post.excerpt}
        image={post.cover_image}
        keywords={post.tags.join(', ')}
      />
      <BreadcrumbSchema items={breadcrumbs} />
      <Navbar />

      {/* Hero Image */}
      <section className="relative h-[50vh] min-h-[400px]">
        <div className="absolute inset-0">
          <img
            src={post.cover_image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/80 via-emerald-950/40 to-transparent" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Link to="/blog" className="inline-flex items-center text-emerald-200 hover:text-white mb-4 text-sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Blog
              </Link>
              <Badge className="bg-gold text-emerald-950 mb-4">{post.category}</Badge>
              <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white mb-4 max-w-4xl">
                {post.title}
              </h1>
              <div className="flex flex-wrap items-center gap-6 text-emerald-200 text-sm">
                <span className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  {post.author}
                </span>
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {format(new Date(post.created_at), 'MMMM d, yyyy')}
                </span>
                <span className="flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  {post.views} views
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="section-padding">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            {/* Excerpt */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-xl text-muted-foreground leading-relaxed mb-8 font-serif italic"
            >
              {post.excerpt}
            </motion.p>

            {/* Content - Using react-markdown for safe rendering */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="prose prose-lg dark:prose-invert max-w-none
                prose-headings:font-serif prose-headings:text-foreground
                prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4
                prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                prose-p:text-muted-foreground prose-p:leading-relaxed prose-p:mb-6
                prose-a:text-emerald-600 prose-a:no-underline hover:prose-a:text-emerald-700
                prose-strong:text-foreground
                prose-ul:text-muted-foreground prose-ol:text-muted-foreground
                prose-li:mb-2
                prose-blockquote:border-emerald-500 prose-blockquote:text-muted-foreground prose-blockquote:italic
                prose-table:border-collapse
                prose-th:bg-muted prose-th:p-3 prose-th:text-left
                prose-td:p-3 prose-td:border-b prose-td:border-border"
            >
              <ReactMarkdown
                components={{
                  h1: ({ children }) => <h1 className="font-serif text-3xl mt-12 mb-4">{children}</h1>,
                  h2: ({ children }) => <h2 className="font-serif text-2xl mt-12 mb-4">{children}</h2>,
                  h3: ({ children }) => <h3 className="font-serif text-xl mt-8 mb-3">{children}</h3>,
                  p: ({ children }) => <p className="text-muted-foreground leading-relaxed mb-6">{children}</p>,
                  a: ({ href, children }) => (
                    <a href={href} className="text-emerald-600 hover:text-emerald-700 underline" target="_blank" rel="noopener noreferrer">
                      {children}
                    </a>
                  ),
                  ul: ({ children }) => <ul className="list-disc pl-6 mb-6 space-y-2 text-muted-foreground">{children}</ul>,
                  ol: ({ children }) => <ol className="list-decimal pl-6 mb-6 space-y-2 text-muted-foreground">{children}</ol>,
                  li: ({ children }) => <li className="text-muted-foreground">{children}</li>,
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-4 border-emerald-500 pl-4 italic text-muted-foreground my-6">
                      {children}
                    </blockquote>
                  ),
                  strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
                  em: ({ children }) => <em className="italic">{children}</em>,
                  table: ({ children }) => (
                    <div className="overflow-x-auto my-6">
                      <table className="w-full border-collapse border border-border">{children}</table>
                    </div>
                  ),
                  th: ({ children }) => <th className="bg-muted p-3 text-left border border-border font-medium">{children}</th>,
                  td: ({ children }) => <td className="p-3 border border-border">{children}</td>,
                }}
              >
                {post.content}
              </ReactMarkdown>
            </motion.div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mt-12 pt-8 border-t border-border">
              <span className="flex items-center gap-2 text-muted-foreground text-sm mr-2">
                <Tag className="w-4 h-4" />
                Tags:
              </span>
              {post.tags.map(tag => (
                <Badge key={tag} variant="outline">{tag}</Badge>
              ))}
            </div>

            {/* Share */}
            <div className="flex items-center gap-4 mt-8">
              <span className="text-muted-foreground text-sm">Share:</span>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-muted flex items-center justify-center hover:bg-emerald-100 dark:hover:bg-emerald-900 transition-colors"
              >
                <Facebook className="w-5 h-5 text-muted-foreground" />
              </a>
              <a
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-muted flex items-center justify-center hover:bg-emerald-100 dark:hover:bg-emerald-900 transition-colors"
              >
                <Twitter className="w-5 h-5 text-muted-foreground" />
              </a>
            </div>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="section-padding bg-muted/30">
          <div className="container-custom">
            <h2 className="font-serif text-2xl text-foreground mb-8 text-center">Related Articles</h2>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {relatedPosts.map((relPost) => (
                <Link key={relPost.id} to={`/blog/${relPost.slug}`} className="group">
                  <div className="aspect-[16/10] rounded-sm overflow-hidden mb-4">
                    <img
                      src={relPost.cover_image}
                      alt={relPost.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <h3 className="font-serif text-lg text-foreground group-hover:text-emerald-700 dark:group-hover:text-emerald-400 transition-colors">
                    {relPost.title}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 bg-emerald-950 text-white text-center">
        <div className="container-custom">
          <h2 className="font-serif text-2xl md:text-3xl mb-4">Ready to Explore?</h2>
          <p className="text-emerald-200 mb-8 max-w-xl mx-auto">
            Book your stay at The Hotel Emerald and experience the beauty of the Eastern Ghats firsthand.
          </p>
          <Link to="/book">
            <Button className="btn-gold">Book Your Stay</Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
